import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import crmApi, { money, STAGE_META, STAGE_ORDER, DOC_STATUS_META, fmtDateTime, fmtDateDMY, CURRENCIES, DEAL_SOURCES, ACTIVITY_TYPES, LOST_REASONS } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

export default function CrmDealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useOutletContext();
  const [deal, setDeal] = useState(null);
  const [note, setNote] = useState('');
  const [activityType, setActivityType] = useState('Meeting');
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [edit, setEdit] = useState(null);
  const [finance, setFinance] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [visibility, setVisibility] = useState('private');

  useEffect(() => { crmApi.get('/users').then(u => setAllUsers(Array.isArray(u) ? u : [])).catch(() => {}); }, []);

  async function openPrivacy() {
    setVisibility(deal.visibility || 'private');
    try { const m = await crmApi.get(`/deals/${id}/members`); setMemberIds(m.map(x => x.user_id)); } catch (e) { setMemberIds([]); }
    setShowPrivacy(true);
  }
  async function savePrivacy() {
    await crmApi.put(`/deals/${id}/visibility`, { visibility, member_ids: memberIds });
    setShowPrivacy(false); load();
  }

  function load() {
    crmApi.get(`/deals/${id}`).then(d => { setDeal(d); }).catch(e => setErr(e?.error || 'Failed'));
    crmApi.get(`/deals/${id}/finance`).then(setFinance).catch(() => {});
  }
  useEffect(() => { load(); }, [id]);
  useLiveRefresh(load);

  const [pendingStage, setPendingStage] = useState(null);   // stage awaiting confirmation
  const [lostReason, setLostReason] = useState('');
  const [lostCat, setLostCat] = useState('');
  const [stageNote, setStageNote] = useState('');

  function requestStage(stage) {
    if (!deal || deal.stage === stage) return;
    if (closed && !isAdmin) return;
    setLostReason(''); setLostCat(''); setStageNote('');
    setPendingStage(stage);
  }

  async function confirmStage() {
    const stage = pendingStage;
    setMsg(null);
    try {
      let lr;
      if (stage === 'lost') {
        if (!lostCat) { setMsg({ type: 'error', text: 'Please choose a reason for the loss.' }); return; }
        lr = lostCat === 'Other' ? (lostReason.trim() || 'Other') : lostCat;
      }
      const r = await crmApi.post(`/deals/${id}/stage`, { stage, lost_reason: lr, note: stageNote || undefined });
      if (r.sales_order) setMsg({ type: 'ok', text: `Deal won — service order ${r.sales_order.doc_number} was created automatically. Open it to review and submit for approval.` });
      setPendingStage(null);
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.error || 'Could not change stage.' });
      setPendingStage(null);
    }
  }

  async function addActivity() {
    if (!note.trim()) return;
    await crmApi.post(`/deals/${id}/note`, { note, activity_type: activityType });
    setNote(''); load();
  }

  function openEdit() {
    setEdit({
      title: deal.title || '', account_id: deal.account_id || '', value: deal.value || 0,
      currency: deal.currency || 'OMR', probability: deal.probability || 0,
      source: deal.source || '', expected_close_date: deal.expected_close_date ? String(deal.expected_close_date).slice(0, 10) : '',
      description: deal.description || '',
    });
    setShowEdit(true);
  }
  async function saveEdit() {
    await crmApi.put(`/deals/${id}`, { ...edit, value: parseFloat(edit.value) || 0 });
    setShowEdit(false); load();
  }

  if (err) return <div style={{ color: 'var(--red)' }}>{err}</div>;
  if (!deal) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;
  const meta = STAGE_META[deal.stage];
  const closed = !!deal.closed;

  return (
    <div>
      <button className="btn" style={{ marginBottom: '14px' }} onClick={() => navigate('/crm/pipeline')}>← Pipeline</button>

      {msg && (
        <div style={{ marginBottom: '14px', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: '13px',
          background: msg.type === 'error' ? 'var(--red-dim)' : 'rgba(13,148,136,0.10)',
          color: msg.type === 'error' ? '#DC2626' : '#0D9488', fontWeight: '600' }}>{msg.text}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '16px', alignItems: 'start' }}>
        {/* Left: detail + documents */}
        <div>
          <div className="card" style={{ padding: '18px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div>
                <h1 style={{ fontSize: '20px', fontWeight: '800' }}>{deal.title}</h1>
                <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>
                  {deal.account_name || 'No account'} · Owner: {deal.owner_name || '—'}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                {!closed && <button className="btn btn-sm" onClick={openEdit}>Edit</button>}
                <button className="btn btn-sm" onClick={openPrivacy} title="Privacy">{deal.visibility === 'public' ? '🌐 Public' : '🔒 Private'}</button>
                <span style={{ fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '99px', background: meta.bg, color: meta.color }}>{meta.label}</span>
                {closed && <span style={{ fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '99px', background: 'var(--surface2)', color: 'var(--muted2)' }}>🔒 Closed</span>}
                {isAdmin && <button className="btn btn-sm" style={{ color: '#DC2626' }} onClick={() => setDelOpen(true)}>Delete</button>}
              </div>
            </div>
            {delOpen && (
              <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setDelOpen(false); }}>
                <div className="modal" style={{ width: '400px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Delete this deal?</h3>
                  <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}><strong>{deal.title}</strong>, its line items, members and activity history will be permanently deleted. Any documents created from it are kept but unlinked. This cannot be undone.</p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button className="btn" onClick={() => setDelOpen(false)}>Cancel</button>
                    <button className="btn" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }}
                      onClick={async () => { try { await crmApi.delete(`/deals/${deal.id}`); navigate('/crm/pipeline'); } catch (e) { setMsg({ type: 'error', text: e?.error || 'Could not delete' }); setDelOpen(false); } }}>Delete deal</button>
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '24px', marginTop: '16px', flexWrap: 'wrap' }}>
              <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700' }}>Value</div><div style={{ fontSize: '17px', fontWeight: '800', color: '#0D9488' }}>{money(deal.value, deal.currency)}</div></div>
              {deal.computed && (deal.computed.gross > 0 || deal.computed.revenue > 0) && <>
                <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700' }}>Sales (from items)</div><div style={{ fontSize: '17px', fontWeight: '800', color: '#2563EB' }}>{money(deal.computed.gross, deal.currency)}</div></div>
                <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700' }}>Revenue (margin)</div><div style={{ fontSize: '17px', fontWeight: '800', color: '#16A34A' }}>{money(deal.computed.revenue, deal.currency)}</div></div>
              </>}
              <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700' }}>Probability</div><div style={{ fontSize: '17px', fontWeight: '800' }}>{deal.probability}%</div></div>
              <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700' }}>Expected Close</div><div style={{ fontSize: '14px', fontWeight: '600', marginTop: '3px' }}>{fmtDateDMY(deal.expected_close_date)}</div></div>
              <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700' }}>Source</div><div style={{ fontSize: '14px', fontWeight: '600', marginTop: '3px' }}>{deal.source || '—'}</div></div>
            </div>
            {deal.description && <p style={{ fontSize: '13px', color: 'var(--muted2)', marginTop: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{deal.description}</p>}

            {/* Stage stepper */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: '700', marginBottom: '7px' }}>Move stage</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {STAGE_ORDER.map(s => (
                  <button key={s} onClick={() => requestStage(s)} disabled={closed && !isAdmin}
                    style={{ fontSize: '11px', fontWeight: '700', padding: '5px 11px', borderRadius: '99px',
                      cursor: (closed && !isAdmin) ? 'not-allowed' : 'pointer', opacity: (closed && !isAdmin) ? 0.5 : 1,
                      border: `1px solid ${deal.stage === s ? STAGE_META[s].color : 'var(--border)'}`,
                      background: deal.stage === s ? STAGE_META[s].color : 'var(--surface)',
                      color: deal.stage === s ? '#fff' : 'var(--muted)' }}>
                    {STAGE_META[s].label}
                  </button>
                ))}
              </div>
              {closed && (
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px' }}>
                  {isAdmin ? 'This deal is closed. As an admin you can reopen it by selecting an earlier stage.' : 'This deal is closed and locked. Only a CRM admin can reopen it.'}
                </div>
              )}
            </div>
          </div>

          {/* Documents linked to this deal */}
          <div className="card" style={{ padding: '18px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: '800' }}>Documents</div>
              <button className="btn btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => navigate(`/crm/quotes/new?deal=${deal.id}&account=${deal.account_id || ''}`)}>+ New Quote</button>
            </div>
            {(!deal.documents || deal.documents.length === 0) && <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No documents yet. Winning this deal will auto-create a service order.</div>}
            {deal.documents?.map(doc => {
              const sm = DOC_STATUS_META[doc.status] || DOC_STATUS_META.draft;
              const route = doc.doc_type === 'invoice' ? 'invoices' : doc.doc_type === 'sales_order' ? 'sales-orders' : 'quotes';
              return (
                <div key={doc.id} onClick={() => navigate(`/crm/${route}/${doc.id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'capitalize', width: '90px' }}>{doc.doc_type.replace('_', ' ')}</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'var(--font-mono)' }}>{doc.doc_number}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: '700' }}>{money(doc.total, doc.currency)}</span>
                  <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 9px', borderRadius: '99px', background: sm.bg, color: sm.color }}>{sm.label}</span>
                </div>
              );
            })}
          </div>

          {/* Deal finance dashboard (shows once invoices exist) */}
          {finance && finance.revenue > 0 && (
            <div className="card" style={{ padding: '18px', marginBottom: '14px' }}>
              <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px' }}>Deal Financials</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Revenue</div><div style={{ fontSize: '16px', fontWeight: '800' }}>{money(finance.revenue, finance.currency)}</div></div>
                <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Cost</div><div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--muted2)' }}>{money(finance.cost, finance.currency)}</div></div>
                <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Margin</div><div style={{ fontSize: '16px', fontWeight: '800', color: finance.gross_margin >= 0 ? '#16A34A' : '#DC2626' }}>{money(finance.gross_margin, finance.currency)}</div></div>
                <div><div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700 }}>Margin %</div><div style={{ fontSize: '16px', fontWeight: '800' }}>{finance.margin_pct_sale}%<span style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 400 }}> sale · {finance.margin_pct_cost}% markup</span></div></div>
              </div>
              {finance.recurring_revenue > 0 && <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '10px' }}>Includes recurring revenue: {money(finance.recurring_revenue, finance.currency)} / month</div>}
            </div>
          )}
        </div>

        {/* Right: Deal History */}
        <div className="card" style={{ padding: '18px' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px' }}>Deal History (Actions)</div>

          {/* Log a team action */}
          <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '12px', marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted2)', marginBottom: '8px' }}>Add an action for this deal</div>
            <select className="select" value={activityType} onChange={e => setActivityType(e.target.value)} style={{ marginBottom: '8px' }}>
              {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <textarea className="input" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Describe what you did (e.g. met the client, discussed pricing)…" style={{ marginBottom: '8px' }} />
            <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={addActivity}>Add action</button>
          </div>

          <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
            {deal.logs?.length === 0 && <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No history yet.</div>}
            {deal.logs?.map(l => (
              <div key={l.id} style={{ display: 'flex', gap: '10px', paddingBottom: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: l.to_stage ? (STAGE_META[l.to_stage]?.color || '#0D9488') : '#0D9488', flexShrink: 0, marginTop: '3px' }} />
                  <div style={{ flex: 1, width: '1px', background: 'var(--border)', marginTop: '3px' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text)' }}>
                    {l.activity_type ? l.activity_type : l.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  {l.note && <div style={{ fontSize: '12px', color: 'var(--muted2)', marginTop: '1px', whiteSpace: 'pre-wrap' }}>{l.note}</div>}
                  <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>{l.user_name} · {fmtDateTime(l.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit deal modal */}
      {showEdit && edit && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowEdit(false); }}>
          <div className="modal" style={{ width: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '16px' }}>Edit Deal</h3>
            <div className="form-group"><label className="form-label">Deal Title</label>
              <input className="input" value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} /></div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Expected Value</label>
                <input className="input" type="number" value={edit.value} onChange={e => setEdit({ ...edit, value: e.target.value })} /></div>
              <div className="form-group" style={{ width: '120px' }}><label className="form-label">Currency</label>
                <select className="select" value={edit.currency} onChange={e => setEdit({ ...edit, currency: e.target.value })}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ width: '120px' }}><label className="form-label">Probability %</label>
                <input className="input" type="number" value={edit.probability} onChange={e => setEdit({ ...edit, probability: e.target.value })} /></div>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">Source</label>
                <select className="select" value={edit.source} onChange={e => setEdit({ ...edit, source: e.target.value })}>
                  <option value="">— Select —</option>
                  {DEAL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select></div>
            </div>
            <div className="form-group"><label className="form-label">Expected Close <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(DD/MM/YYYY)</span></label>
              <input className="input" type="date" value={edit.expected_close_date} onChange={e => setEdit({ ...edit, expected_close_date: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Notes / Description</label>
              <textarea className="input" rows={3} value={edit.description} onChange={e => setEdit({ ...edit, description: e.target.value })} /></div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setShowEdit(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
      {/* Privacy modal */}
      {showPrivacy && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowPrivacy(false); }}>
          <div className="modal" style={{ width: '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '16px' }}>Deal Privacy</h3>
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <select className="select" value={visibility} onChange={e => setVisibility(e.target.value)}>
                <option value="private">Private — only assigned people, the owner and admins</option>
                <option value="public">Public — everyone in the CRM can see it</option>
              </select>
            </div>
            {visibility === 'private' && (
              <div className="form-group">
                <label className="form-label">Who can access this deal</label>
                <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px' }}>
                  {allUsers.map(u => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '4px 6px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={memberIds.includes(u.id)}
                        onChange={e => setMemberIds(ids => e.target.checked ? [...ids, u.id] : ids.filter(x => x !== u.id))} />
                      {u.full_name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setShowPrivacy(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePrivacy}>Save</button>
            </div>
          </div>
        </div>
      )}
      {/* Stage change confirmation */}
      {pendingStage && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setPendingStage(null); }}>
          <div className="modal" style={{ width: '420px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '10px' }}>
              {pendingStage === 'won' ? 'Mark deal as Won?' : pendingStage === 'lost' ? 'Mark deal as Lost?' : 'Move deal stage?'}
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '14px' }}>
              You are moving <strong>{deal.title}</strong> from <strong>{STAGE_META[deal.stage]?.label}</strong> to <strong>{STAGE_META[pendingStage]?.label}</strong>.
              {(pendingStage === 'won' || pendingStage === 'lost') && ' Once closed, only a CRM admin can move it again.'}
              {pendingStage === 'won' && ' A draft service order will be created automatically.'}
            </p>
            {pendingStage === 'lost' && (
              <div className="form-group">
                <label className="form-label">Reason for loss <span style={{ color: '#DC2626' }}>*</span></label>
                <select className="select" value={lostCat} onChange={e => setLostCat(e.target.value)}>
                  <option value="">Select a reason…</option>
                  {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {lostCat === 'Other' && (
                  <textarea className="input" rows={2} value={lostReason} onChange={e => setLostReason(e.target.value)} placeholder="Describe the reason" style={{ marginTop: '8px' }} />
                )}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Note <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional — added to deal history)</span></label>
              <textarea className="input" rows={2} value={stageNote} onChange={e => setStageNote(e.target.value)} placeholder="Add a comment about this move…" />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => setPendingStage(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmStage}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
